import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import addEventImage from "../assets/addevent.jpg"; // alap illusztráció
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import { Hungarian } from "flatpickr/dist/l10n/hu.js";
import { geocodeLocation } from "../utils/geocode";

// Egy közösség ID-t ad vissza név alapján: meglévőt keres, különben beszúr.
async function ensureCommunityId(supabase, name) {
  const n = (name || "").trim();
  if (!n) return null;

  // 1) Megpróbáljuk megtalálni (case-insensitive)
  let { data: found, error: selErr } = await supabase
    .from("communities")
    .select("id")
    .ilike("name", n)
    .maybeSingle();

  // PGRST116 = no rows, ez nem hiba
  if (selErr && selErr.code !== "PGRST116") throw selErr;
  if (found?.id) return found.id;

  // 2) Ha nincs, beszúrjuk
  let { data: ins, error: insErr } = await supabase
    .from("communities")
    .insert({ name: n })
    .select("id")
    .single();

  // 23505 = unique violation → közben valaki már létrehozta
  if (insErr?.code === "23505") {
    const { data: again } = await supabase
      .from("communities")
      .select("id")
      .ilike("name", n)
      .maybeSingle();
    return again?.id ?? null;
  }
  if (insErr) throw insErr;

  return ins?.id ?? null;
}


const STORAGE_BUCKET = "event-images";

/** ékezetek és speciális karakterek eltávolítása, csak [a-z0-9._-] marad */
const sanitizeFilename = (name) =>
  (name || "")
    .toLowerCase()
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

/** fix fallback mapping */
const FALLBACK_MAP = {
  "mindenki": "general.jpg",
  "fiatalok": "fiatalok.jpg",
  "idősek": "idosek.jpg",
  "fiatal házasok": "fiatal_hazasok.jpg",
  "érett házasok": "erett_hazasok.jpg",
  "jegyesek": "jegyesek.jpg",
  "tinédzserek": "tinedzserek.jpg",
  "családok": "csaladok.jpg",
  "férfiak":"ferfiak_1.jpg",
  "nők":"nok_1.jpg",
};

function getFallbackImage(group) {
  const key = (group || "").toLowerCase();
  const file = FALLBACK_MAP[key] || "general.jpg";
  return `https://kibgskyyevsighwtkqcf.supabase.co/storage/v1/object/public/event-images/fallback/${file}`;
}

export default function AddEventForm({ currentUser, onCancel, onSuccess, goHome }) {
  // űrlapmezők
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [targetGroup, setTargetGroup] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [contact, setContact] = useState("");
  const [communityId, setCommunityId] = useState("");
  const [registrationLink, setRegistrationLink] = useState("");

  const [communities, setCommunities] = useState([]);

  const [newCommunityName, setNewCommunityName] = useState("");
  const [addingCommunity, setAddingCommunity] = useState(false);
  // poszter
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState("");
  const [posterUrl, setPosterUrl] = useState("");
  const [posterUploading, setPosterUploading] = useState(false);

  // Flatpickr change handlerek – ISO-ra tesszük a state-et
  const onStartChange = (dates) => {
    const d = dates?.[0] || null;
    setStartDate(d ? d.toISOString() : "");
  };
  const onEndChange = (dates) => {
    const d = dates?.[0] || null;
    setEndDate(d ? d.toISOString() : "");
  };

  // egyéb
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // auth user – ha a szülő nem adta át, lekérjük
  const [authUser, setAuthUser] = useState(currentUser || null);
  useEffect(() => {
    let alive = true;
    if (!currentUser) {
      supabase.auth.getUser().then(({ data }) => {
        if (alive) setAuthUser(data?.user || null);
      });
    }
    return () => { alive = false; };
  }, [currentUser]);

  // közösségek
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from("communities").select("id,name").order("name");
      if (!error && Array.isArray(data)) setCommunities(data);
    })();
  }, []);

  // poszter validáció
  const validatePosterFile = (file) => {
    if (!file) return null;
    if (file.size > 5 * 1024 * 1024) return "A poszter túl nagy (max. 5 MB).";
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) return "Csak JPG, PNG vagy WEBP engedélyezett.";
    return null;
  };

  // poszter feltöltés Storage-ba
  async function uploadPoster(file) {
    if (!file) return null;
    const v = validatePosterFile(file);
    if (v) throw new Error(v);

    setPosterUploading(true);
    try {
      const clean = sanitizeFilename(file.name) || `poster-${Date.now()}.png`;
      const path = `posters/${Date.now()}_${clean}`; // nincs vezető perjel!

      const { error: upErr } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(path, file, {
          upsert: true,
          cacheControl: "31536000",
          contentType: file.type || "image/*",
        });
      if (upErr) throw upErr;

      const { data: pub } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
      const url = pub?.publicUrl || null;
      if (!url) throw new Error("Nem sikerült publikus URL-t előállítani a poszterhez.");
      setPosterUrl(url);
      return url;
    } finally {
      setPosterUploading(false);
    }
  }

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (saving) return;
  setError(null);
  setSaving(true);

  try {
    // 1) poszter URL (ha még nem töltötted fel külön gombbal)
    let finalPosterUrl = posterUrl;
    if (posterFile && !posterUrl) {
      finalPosterUrl = await uploadPoster(posterFile);
    }

    // 2) közösség feloldása
    let resolvedCommunityId = null;
    if (communityId) {
      resolvedCommunityId = Number(communityId);
    } else if ((newCommunityName || "").trim()) {
      resolvedCommunityId = await ensureCommunityId(supabase, newCommunityName);
      if (resolvedCommunityId) {
        setCommunities((prev) =>
          [...prev, { id: resolvedCommunityId, name: (newCommunityName || "").trim() }].sort(
            (a, b) => a.name.localeCompare(b.name)
          )
        );
        setCommunityId(String(resolvedCommunityId));
        setNewCommunityName("");
      }
    }

    // 3) payload összeállítása
    let payload = {
      title,
      description,
      location,
      target_group: targetGroup || "Mindenki",
      start_date: startDate || null,
      end_date: endDate || null,
      contact,
      community_id: resolvedCommunityId,
      registration_link: registrationLink || null,
      poster_url: finalPosterUrl || null,
      source: "manual",
      created_by: authUser?.id || currentUser?.id || null,
    };

    // 4) ha nincs lat/lng, próbáljuk geokódolni a locationt
    if (!payload.lat && !payload.lng && payload.location) {
      const coords = await geocodeLocation(payload.location);
      if (coords) {
        payload = { ...payload, lat: coords.lat, lng: coords.lng };
      }
    }

    // 5) mentés
    const { data, error } = await supabase
      .from("events")
      .insert(payload)
      .select("id")
      .single();

    if (error) throw error;

    onSuccess && onSuccess(data?.id);
    if (typeof goHome === "function") {
      goHome();
    }
  } catch (err) {
    setError(err?.message || "Hiba a mentés közben.");
  } finally {
    setSaving(false);
  }
};



  const onPosterChange = (e) => {
    const f = e.target.files?.[0] || null;
    setPosterFile(f);
    setPosterPreview(f ? URL.createObjectURL(f) : "");
  };

  return (
    <form onSubmit={handleSubmit} className="w-100" noValidate autoComplete="off">
      <div className="row g-4 align-items-start">
        {/* BAL OSZLOP – Kép előnézet */}
        <div className="col-12 col-md-5" style={{ position: "sticky", top: 16 }}>
          <div className="border rounded-3 bg-light overflow-hidden w-100" style={{ height: 260 }}>
            <img
              src={
                posterPreview ||
                posterUrl ||
                (targetGroup ? getFallbackImage(targetGroup) : addEventImage)
              }
              alt="Illusztráció / poszter"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = addEventImage; // végső visszaesés
              }}
            />
          </div>
          <small className="text-muted d-block mt-2">Illusztráció / poszter előnézet</small>
        </div>

        {/* JOBB OSZLOP – Mezők + poszter feltöltés */}
        <div className="col-12 col-md-7">
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="mb-3">
            <label className="form-label">Megnevezés *</label>
            <input
              className="form-control"
              placeholder="Pl. Fiatalok lelkigyakorlata"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Leírás *</label>
            <textarea
              className="form-control"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Helyszín *</label>
            <input
              className="form-control"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Célcsoport *</label>
            <select
              className="form-select"
              value={targetGroup}
              onChange={(e) => setTargetGroup(e.target.value)}
              required
            >
              <option value="">Válassz célcsoportot</option>
              <option>Mindenki</option>
              <option>Fiatalok</option>
              <option>Idősek</option>
              <option>Fiatal házasok</option>
              <option>Érett házasok</option>
              <option>Jegyesek</option>
              <option>Tinédzserek</option>
              <option>Családok</option>
			  <option>Férfiak</option>
			  <option>Nők</option>
            </select>
          </div>

          <div className="row g-3">
		  <div className="col-12 col-md-6">
			<label className="form-label">Kezdés *</label>
			<Flatpickr
			  value={startDate ? new Date(startDate) : null}
			  onChange={onStartChange}
			  className="form-control"
			  options={{
				enableTime: true,
				time_24hr: true,
				// a Hungarian helyett/ mellett erőből megadjuk:
				locale: { firstDayOfWeek: 1 },
				altInput: true,
				altFormat: "Y.m.d H:i",
				dateFormat: "Z",
			  }}
			/>
		  </div>
		  <div className="col-12 col-md-6">
			<label className="form-label">Befejezés</label>
			<Flatpickr
			  value={endDate ? new Date(endDate) : null}
			  onChange={onEndChange}
			  className="form-control"
			  options={{
				enableTime: true,
				time_24hr: true,
				locale: Hungarian,
				altInput: true,
				altFormat: "Y.m.d H:i",
				dateFormat: "Z",
			  }}
			/>
		  </div>
		</div>


          <div className="mb-3 mt-3">
            <label className="form-label">Kapcsolattartó (Név és e-mail vagy telefonszám)</label>
            <input
              className="form-control"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              required
            />
          </div>

          <div className="mb-3">
		  <label className="form-label">Szervező közösség</label>
		  <select
			className="form-select"
			value={communityId}
			onChange={(e) => setCommunityId(e.target.value)}
		  >
			<option value="">Kezdd el írni a közösség nevét…</option>
			{communities.map((c) => (
			  <option key={c.id} value={String(c.id)}>{c.name}</option>
			))}
		  </select>

		  {/* Új közösség hozzáadása inline */}
		  <div className="mt-2 d-flex gap-2">
			<input
			  type="text"
			  className="form-control"
			  placeholder="Új közösség neve…"
			  value={newCommunityName || ""}
			  onChange={(e) => setNewCommunityName(e.target.value)}
			/>
			<button
			  type="button"
			  className="btn btn-outline-primary"
			  disabled={!newCommunityName || addingCommunity}
			  onClick={async () => {
				try {
				  setAddingCommunity(true);
				  const name = (newCommunityName || "").trim();
				  if (!name) return;
				  const { data, error } = await supabase
					.from("communities")
					.insert({ name })
					.select("id,name")
					.single();
				  if (error) throw error;
				  // hozzáadjuk a listához és kiválasztjuk
				  setCommunities((prev) => [...prev, data].sort((a,b) => a.name.localeCompare(b.name)));
				  setCommunityId(String(data.id));
				  setNewCommunityName("");
				} catch (e) {
				  setError(e?.message || "Nem sikerült a közösséget hozzáadni.");
				} finally {
				  setAddingCommunity(false);
				}
			  }}
			  title="Új közösség felvétele"
			>
			  {addingCommunity ? "Hozzáadás…" : "Hozzáadás"}
			</button>
		  </div>
		  <small className="text-muted">Ha nem találod a listában, itt felveheted és kiválaszthatod.</small>
		</div>


			<div className="mb-3">
			  <label className="form-label">Jelentkezési elérhetőség *</label>
			  <input
				type="text"
				className="form-control"
				value={registrationLink}
				onChange={(e) => setRegistrationLink(e.target.value)}
				placeholder="E-mail cím vagy Név és telefonszám"
				required
			  />
			</div>



          {/* Poszter feltöltés */}
          <div className="mb-3 border rounded p-3 bg-light">
            <label className="form-label">Poszter feltöltése (opcionális)</label>
            <div className="d-flex align-items-center gap-2">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onPosterChange}
                className="form-control"
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={!posterFile || posterUploading}
                onClick={async () => {
                  try { await uploadPoster(posterFile); }
                  catch (e) { setError(e?.message || "Hiba a poszter feltöltésekor."); }
                }}
              >
                {posterUploading ? "Feltöltés…" : "Feltöltés"}
              </button>
              {posterUrl && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => { setPosterUrl(""); setPosterPreview(""); setPosterFile(null); }}
                  title="Poszter eltávolítása"
                >
                  Eltávolítás
                </button>
              )}
            </div>
            <small className="text-muted d-block mt-2">
              Ajánlott: 1200×630 (megosztáshoz), &lt; 5 MB, JPG/PNG/WebP.
            </small>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>Mégsem</button>
            <button type="submit" className="btn btn-primary" disabled={saving || posterUploading}>
              {saving ? "Hozzáadás…" : "Hozzáadás"}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

